import { useState } from 'react';

import { useIsFetching, useQueryClient } from '@tanstack/react-query';

import { Button } from '@components/button';
import { ButtonGroup } from '@components/button-group';
import { Card } from '@components/card';
import { createForm, useCreateFormStore } from '@components/form';
import { IconEdit } from '@components/icons/edit';
import { IconEmail } from '@components/icons/email';
import { IconFormIndicatorError } from '@components/icons/form-indicator-error';
import { IconFormIndicatorInfo } from '@components/icons/form-indicator-info';
import { IconFormIndicatorSuccess } from '@components/icons/form-indicator-success';
import { IconOutput } from '@components/icons/output';
import { Separator } from '@components/separator';
import { Sidebar, hideSidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { useEditEmailSettings, useVerifyEmailSettings } from 'api/company/hooks';
import { EmailSettings } from 'api/company/requests';

export const useUnmanagedEmailDomainFormSidebar = () =>
  useSidebar(UnmanagedEmailDomainForm, { variant: 'REGULAR' });

const Form = createForm<EmailSettings>();

const UnmanagedEmailDomainForm = ({
  smtpHost,
  smtpPort,
  smtpLogin,
  fromAddress,
  fromName,
  isVerified: isCurrentlyVerified,
}: {
  smtpHost?: string;
  smtpPort?: string;
  smtpLogin?: string;
  fromAddress?: string;
  fromName?: string;
  isVerified?: boolean;
}) => {
  const { t } = useScopedTranslate(locale);
  const submitMutation = useEditEmailSettings();
  const verifyMutation = useVerifyEmailSettings();
  const queryClient = useQueryClient();
  const isFetching = useIsFetching({ queryKey: ['company-email-settings'] }) > 0;
  const isEditing = Boolean(smtpHost);
  const formStore = useCreateFormStore();
  const values = formStore.useFormValues();
  const [isVerified, setIsVerified] = useState<boolean | undefined>(isCurrentlyVerified);

  const submit = async (values: EmailSettings) => {
    await submitMutation.mutateAsync({ ...values, isManaged: false });
    await queryClient.refetchQueries(['company-email-settings']);
    hideSidebar();
  };

  const handleTestRequest = async () => {
    const response = await verifyMutation.mutateAsync({
      smtpHost: values.smtpHost as string,
      smtpPort: values.smtpPort as number,
      smtpLogin: values.smtpLogin as string,
      smtpPassword: values.smtpPassword as string,
    });
    setIsVerified(response.isVerified);
  };

  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        icon={isEditing ? IconEdit : IconEmail}
        iconColor="elementBlue100"
        title={isEditing ? t('editEmailDomain') : t('addEmailDomain')}
      />
      <Sidebar.Content>
        <Form onSubmit={submit} store={formStore}>
          <Stack space={6}>
            <Text color="textSecondary" font="regular14" text={t('description')} />
            <Stack space={4}>
              <Form.TextField
                id="smtpHost"
                label={t('emailSettingsSmtpHost')}
                validators={['required']}
                placeholder={t('smtpHostPlaceholder')}
                defaultValue={smtpHost}
                onChange={() => setIsVerified(undefined)}
              />
              <Form.TextField
                id="smtpPort"
                label={t('emailSettingsSmtpPort')}
                validators={['required', 'number']}
                placeholder={t('smtpPortPlaceholder')}
                defaultValue={smtpPort ?? '587'}
                onChange={() => setIsVerified(undefined)}
              />
              <Form.TextField
                id="smtpLogin"
                label={t('emailSettingsSmtpLogin')}
                validators={['required']}
                placeholder={t('smtpLoginPlaceholder')}
                defaultValue={smtpLogin}
                onChange={() => setIsVerified(undefined)}
              />
              <Form.PasswordField
                id="smtpPassword"
                label={
                  isEditing ? t('emailSettingsSmtpPasswordEditing') : t('emailSettingsSmtpPassword')
                }
                validators={isEditing ? [] : ['required']}
                placeholder={t('smtpPasswordPlaceholder')}
                onChange={() => setIsVerified(undefined)}
              />
              <Separator />
              <Form.TextField
                id="fromAddress"
                label={t('emailSettingsFromAddress')}
                validators={['required']}
                placeholder={t('fromAddressPlaceholder')}
                defaultValue={fromAddress}
              />
              <Form.TextField
                id="fromName"
                label={t('emailSettingsFromName')}
                validators={['required', 'email-sender-name']}
                placeholder={t('fromNamePlaceholder')}
                defaultValue={fromName}
              />
              <Card space={4}>
                <Stack space={4}>
                  <div>
                    <Text font="semibold14" text={t('checkCredentials')} />
                    <Text font="regular14" text={t('checkCredentialsDescription')} />
                  </div>
                  {!isVerified && (
                    <ButtonGroup align="left">
                      <Button
                        iconLeft={<IconOutput />}
                        text={t('testCredentials')}
                        variant="primary-blue"
                        onClick={handleTestRequest}
                        isDisabled={verifyMutation.isLoading}
                      />
                    </ButtonGroup>
                  )}
                  <Stack orientation="horizontal" align="center" space={2}>
                    <VerifiedStatus isVerified={isVerified} />
                  </Stack>
                </Stack>
              </Card>
            </Stack>
          </Stack>
          <Sidebar.Footer>
            <Form.Actions>
              <Button
                text={t('core.cancel')}
                isDisabled={submitMutation.isLoading || isFetching}
                onClick={hideSidebar}
                variant="secondary"
              />
              <Form.Submit
                text={isEditing ? t('core.save') : t('addDomain')}
                variant="primary-blue"
                isDisabled={submitMutation.isLoading || isFetching}
              />
            </Form.Actions>
          </Sidebar.Footer>
        </Form>
      </Sidebar.Content>
    </Sidebar>
  );
};

const VerifiedStatus = ({ isVerified }: { isVerified: boolean | undefined }) => {
  const { t } = useScopedTranslate(locale);
  switch (isVerified) {
    case true:
      return (
        <>
          <IconFormIndicatorSuccess />
          <Text color="green100" font="regular14" text={t('verifiedCredentials')} />
        </>
      );
    case false:
      return (
        <>
          <IconFormIndicatorError />
          <Text color="elementRed100" font="regular14" text={t('invalidCredentials')} />
        </>
      );
    case undefined:
    default:
      return (
        <>
          <IconFormIndicatorInfo color="indigo100" />
          <Text color="indigo100" font="regular14" text={t('notVerifiedCredentials')} />
        </>
      );
  }
};

const locale = {
  en: {
    addEmailDomain: 'Add email domain (SMTP)',
    editEmailDomain: 'Edit email domain (SMTP)',
    emailSettings: 'Email settings',
    emailSettingsDescription: 'Configure your email settings',
    emailSettingsToggle: 'Enable custom email settings',
    emailSettingsSmtpHost: 'SMTP host',
    emailSettingsSmtpPort: 'SMTP port',
    emailSettingsSmtpLogin: 'SMTP login',
    emailSettingsSmtpPassword: 'SMTP password',
    emailSettingsSmtpPasswordEditing: 'SMTP password (leave blank to keep current password)',
    emailSettingsFromAddress: 'Send from address',
    emailSettingsFromName: 'Send from name',
    emailDomainPlaceholder: 'acme.com',
    smtpHostPlaceholder: 'smtp.acme.com',
    smtpPortPlaceholder: '587',
    smtpLoginPlaceholder: 'Usually an email address',
    smtpPasswordPlaceholder: 'Password',
    fromAddressPlaceholder: 'E.g. journeys@acme.com',
    fromNamePlaceholder: 'Acme Inc.',
    checkCredentials: 'Check credentials',
    checkCredentialsDescription: 'Press the Test credentials button below to test your credentials',
    testCredentials: 'Test credentials',
    notVerifiedCredentials: 'Credentials not verified',
    verifiedCredentials: 'Credentials verified',
    invalidCredentials: 'Credentials invalid',
    validateBefore: 'Please validate your credentials before saving',
    addDomain: 'Add domain',
    description: 'Please refer to your email provider for the SMTP settings',
  },
};
